import { createContext, useContext, useState, ReactNode } from "react";

export type LanguageCode = "en" | "sv" | "no" | "da" | "fi";

export const languages = [
  { code: "en" as LanguageCode, name: "English", flag: "us" },
  { code: "sv" as LanguageCode, name: "Svenska", flag: "se" },
  { code: "no" as LanguageCode, name: "Norsk", flag: "no" },
  { code: "da" as LanguageCode, name: "Dansk", flag: "dk" },
  { code: "fi" as LanguageCode, name: "Suomi", flag: "fi" },
];

const LANGUAGE_STORAGE_KEY = "veritas-language";

const isValidLanguageCode = (code: string): code is LanguageCode =>
  languages.some((l) => l.code === code);

const loadStoredLanguage = (): LanguageCode => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return stored && isValidLanguageCode(stored) ? stored : "en";
};

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  currentLanguage: typeof languages[0];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<LanguageCode>(loadStoredLanguage);
  
  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  const setLanguage = (lang: LanguageCode) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    setLanguageState(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, currentLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

